import React, { useEffect, useState } from "react"

import { 
  Form, 
  Input, 
  Row, 
  Col, 
  Select, 
  Button, 
  AutoComplete, 
  Spin,
  Typography,
  Checkbox,
  message,
  Tooltip,
  Radio,
} from "antd"

import { PlusCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';


import styled from 'styled-components'

import { useSearchMedications } from "../hooks/useSearchMedications";
import { usePharmacies } from "../../../../../hooks/usePharmacies";
import { useLookup } from "../../../../../hooks/useLookup";
import { useTelemedicineCategoryContext } from "../../../../../context/TelemedicineCategoryContext";
import { findMiscasedSpecialVariables } from "@veritas/case-view/utils/pharmacyNoteUtils";

const { TextArea } = Input

const StyledFormItem = styled(Col)`
  & label {
    text-transform: capitalize;
  }
`

const StyledButtonGroup = styled(Row)`
    width: 100%;
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;

    & button {
        width: 100px;
        background: #fff;
        color: #171c2f;
    }
`

const StyledTooltip = styled(Tooltip)`
  & div[role="tooltip"] {
    color: #fff;
  }
`

const TemplateKeyWrapper = styled.div`
  max-width: 360px;
  color: #fff;
  text-transform: none;
`

const TemplateKeyTitle = styled.strong`
  color: #fff;
`

const TemplateKeyTable = styled.table`
  border-collapse: collapse;
  width: 100%;
`

const TemplateKeyPlaceholderCell = styled.td`
  padding-right: 10px;
  padding-bottom: 3px;
  white-space: nowrap;
  vertical-align: top;
`

const TemplateKeyCode = styled.code`
  background: rgba(255, 255, 255, 0.15);
  padding: 1px 4px;
  border-radius: 3px;
  font-family: monospace;
`

const TemplateKeyDescCell = styled.td`
  padding-bottom: 3px;
  color: rgba(255, 255, 255, 0.85);
`

const TemplateKeyIcon = styled(QuestionCircleOutlined)`
  margin-left: 6px;
  color: #7bad7e;
  cursor: help;
`

// This form intentionally surfaces only {{CDS}} in the help tooltip because
// it's the single template variable relevant to the per-prescription-template
// pharmacyNotes field. The full canonical catalog lives in
// @veritas/case-view/utils/pharmacyNoteUtils (TEMPLATE_VARIABLE_DESCRIPTIONS)
// and is shared with the provider Edit Treatment modal.
const TEMPLATE_VARIABLE_DESCRIPTIONS = [
    { variable: '{{CDS}}',           description: "Clinical Decision Support option selected by the provider from the telemedicine category's CDS list" },
]

const PharmacyNotesTemplateKey = () => (
    <TemplateKeyWrapper>
        <TemplateKeyTitle>Available template variables</TemplateKeyTitle>
        <div style={{ margin: '6px 0' }}>
            Wrap any of the following, or a custom variable in double curly braces to require a value from a provider before the prescription is sent to the pharmacy.
        </div>
        <TemplateKeyTable>
            <tbody>
                {TEMPLATE_VARIABLE_DESCRIPTIONS.map(({ variable, description }) => (
                    <tr key={variable}>
                        <TemplateKeyPlaceholderCell>
                            <TemplateKeyCode>{variable}</TemplateKeyCode>
                        </TemplateKeyPlaceholderCell>
                        <TemplateKeyDescCell>{description}</TemplateKeyDescCell>
                    </tr>
                ))}
            </tbody>
        </TemplateKeyTable>
    </TemplateKeyWrapper>
)

const FormItem = ({ span, children, ...props }) => (
  <StyledFormItem span={span || 12}>
    <Form.Item {...props} label={props.label || props.name}>
      {children}
    </Form.Item>
  </StyledFormItem>
);

export const PrescriptionTemplateForm = ({
  submit,
  selectedTemplate,
  isDuplicate,
  handleFinish
}) => {

  const [showQRD, setShowQRD] = useState(false)
  const [subtype, setSubtype] = useState(selectedTemplate?.subtype || 'medication')
  const allStates = useLookup('state')

  const isSupply = subtype === 'supply'

  const [states, setStates] = useState({
    states: [],
    allStateOptions: [],
    selectedStates: [],
    searchMatches: [],
  })

  const {
    telemedicineCategories,
    getTelemedicineCategoryIdByName,
  } = useTelemedicineCategoryContext()

  const [form] = Form.useForm();

  const {
    fetchMedications,
    medicationSearchMatches,
    availableMedicationStrengths,
    medicationSearchLoading,
    handleSelectMedication,
    handleSelectStrength,
    setMedicationSearchLoading,
    setMedicationSearchMatches,
    getTemplateMedicationAndStrength,
    getTemplateSupply,
  } = useSearchMedications({
    form,
    selectedTemplate,
    subtype,
  })

  const handleSubTypeChange = (e) => {
    const newSubType = e.target.value
    setSubtype(newSubType)
    setMedicationSearchMatches([])
    form.setFieldsValue({
      drugName: null,
      ndc: null,
      strength: null,
      dispensableDrugId: null,
      supplyId: null,
      ingredients: null,
      dispenseUnitId: null,
      schedule: null,
    })
  }

  const {
    pharmacySearchResults,
    pharmaciesLoading,
    fetchPharmacy,
    selectPharmacyById,
    selectPharmacy,
    selectedPharmacy,
    searchPharmacies
  } = usePharmacies()

  const telemedicineCategoryOptions = telemedicineCategories.map(category => ({
    label: category.name,
    value: category.name,
    key: category._id,
  }))



  const handleSelectAllStates = () => {
    setStates(prev => ({
      ...prev,
      selectedStates: states.allStateOptions.map(state => state.value)
    }))
  }

  const handleSelectState = state => {
    setStates(prev => ({
      ...prev,
      selectedStates: [...prev.selectedStates, state],
      searchMatches: states.allStateOptions
    }))
  }

  const handleDeselectState = state => {
    setStates(prev => ({
      ...prev,
      selectedStates: prev.selectedStates.filter(selectedState => selectedState !== state)
    }))
  }

  const handleClearStates = () => {
   setStates(prev => ({
     ...prev,
     selectedStates: []
   }))
  }

  const handleSearchStates = searchStr => {
    const searchMatches = states.allStateOptions.filter(state => state.label.toLowerCase().includes(searchStr.toLowerCase()))
    setStates(prev => ({
      ...prev,
      searchMatches
    }))
  }

  const handleSubmit = async () => {
    const values = form.getFieldsValue()

    if (Number.isNaN(values.quantity)) {
      return message.error('Quantity must be a number')
    }

    if (!values.qrd) {
      values.refills = null
      values.pillCount = null
      values.daysSupply = null
    }

    const teleMedicineCategoryIds = values.teleMedicineCategories.map(getTelemedicineCategoryIdByName)
    const templateStates = states.selectedStates.map(selectedState => states.states.find(stateObj => selectedState === stateObj.name))

    let medicationName

    if (isSupply) {
      if (!values.supplyId) {
        return message.error('Please select a supply before saving.')
      }

      const supply = await getTemplateSupply(values.supplyId)
      medicationName = supply?.name || values.drugName
    } else {
      if (!values.dispensableDrugId && values.strength?.toLowerCase() !== 'compound') {
        return message.error('Malformed template. Please retry.')
      }

      if (values.dispensableDrugId) {
        const medication = await getTemplateMedicationAndStrength(values.dispensableDrugId)
        if (!medication) return message.error('Medication not found.')
        medicationName = medication.displayName || `${medication.name} ${medication.strength}`
      } else {
        medicationName = values.drugName
      }
    }

    if (!window.confirm(`Confirm creation of template for ${medicationName} ?`)) return

    submit({
      ...values,
      subtype,
      states: templateStates,
      pharmacyId: selectedPharmacy?.pharmacyId || null,
      pharmacyName: selectedPharmacy?.name || null,
      teleMedicineCategoryIds,
      setLoading: setMedicationSearchLoading
    })

    setMedicationSearchMatches([])
    form.resetFields()
  }

  const _handleFinish = () => {
    form.resetFields()
    handleFinish()
  }

  useEffect(() => {
    if (allStates) {
        const allStateOptions = allStates.map(state => ({
          label: state.name,
          value: state.name
        }))

      setStates(prev => {
        return {
          ...prev,
          states: allStates,
          searchMatches: allStateOptions,
          allStateOptions,
          selectedStates: selectedTemplate 
            ? selectedTemplate.states?.map(state => state.name)
            : []
        }
      })
    }
  }, [selectedTemplate, allStates])

  useEffect(() => {
    form.resetFields()
    setSubtype(selectedTemplate?.subtype || 'medication')

    if (selectedTemplate) {
      const { medication } = selectedTemplate

      const hasQuantityRefillsOrDaysSupply = (
        !isNaN(parseInt(medication.pillCount)) ||
        !isNaN(parseInt(medication.refills)) ||
        !isNaN(parseInt(medication.daysSupply))
      ) 

      setShowQRD(hasQuantityRefillsOrDaysSupply)

      form.setFieldsValue({
        ...selectedTemplate,
        ...(selectedTemplate.medication || {}),
        teleMedicineCategories: selectedTemplate.teleMedicineCategoryIds?.map(({ name }) => name),
        qrd: hasQuantityRefillsOrDaysSupply
      })

      if (selectedTemplate.pharmacyId) {
        fetchPharmacy(selectedTemplate.pharmacyId)
          .then(pharmacy => {
            selectPharmacy(pharmacy)
            form.setFieldsValue({ pharmacy: pharmacy.name })
          })
      }
    } else {
      form.setFieldsValue({
        qrd: false
      })
    }

    
    return () => form.resetFields()
  }, [selectedTemplate])

  const medicationOptions = medicationSearchMatches?.map(({ name }) => ({ value: name }))
  const availableMedicationStrengthsOptions = availableMedicationStrengths?.map(({ strength }) => ({ value: strength }))
  const pharmacyOptions = pharmacySearchResults?.map(({ pharmacyId, name, city, state }) => ({ label: `${name}, ${city} ${state}`, value: pharmacyId, key: pharmacyId }))

  return (
    <Spin spinning={medicationSearchLoading}>
      <Form
        form={form}
        layout="vertical"
        style={{ textTransform: 'capitalize' }}
        validateMessages={{ required: 'required' }}
        onFinish={handleSubmit}
      >
        <Row gutter={16}>
          <FormItem required span={24} name="title" label="Template Name">
            <Input id="prescription-template-form-title-input" placeholder="Template Name" />
          </FormItem>
          <StyledFormItem span={24}>
            <Form.Item label="Template Type">
              <Radio.Group id="prescription-template-form-type-group" value={subtype} onChange={handleSubTypeChange}>
                <Radio.Button id="prescription-template-form-type-medication-radio" value="medication">Medication</Radio.Button>
                <Radio.Button id="prescription-template-form-type-supply-radio" value="supply">Supply</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </StyledFormItem>
          <FormItem required span={24} name="drugName" label={isSupply ? "Supply" : "Medication"}>
            <AutoComplete
              id="prescription-template-form-drug-name-autocomplete"
              onSelect={handleSelectMedication}
              onSearch={fetchMedications}
              placeholder={isSupply ? "Search supply name" : "Search medication name"}
              options={medicationOptions}
            />
          </FormItem>
          {!isSupply && (
            <FormItem
              span={24}
              label="Pseudonyms"
              name="pseudonyms"
            >
              <Select
                id="prescription-template-form-pseudonyms-select"
                mode="tags"
                style={{ width: '100%' }}
                placeholder="Alternative names for requested medication"
              />
            </FormItem>
          )}
          <FormItem
            span={24}
            label="Telemedicine Categories"
            name="teleMedicineCategories"
            rules={[{ required: true }]}
          >
            <Select
              id="prescription-template-form-categories-select"
              mode="multiple"
              allowClear
              placeholder={!!selectedTemplate ? "None selected" : "Select Telemedicine Categories"}
              options={telemedicineCategoryOptions}
            />
          </FormItem>
          <FormItem
            span={24}
            label="States"
            name="states"
          >
            <div style={{ display: 'flex', gap: '10px'}}>
              <Select
                id="prescription-template-form-states-select"
                mode="multiple"
                onSearch={handleSearchStates}
                onSelect={handleSelectState}
                onDeselect={handleDeselectState}
                options={states.searchMatches}
                value={states.selectedStates}
                autoClearSearchValue
                placeholder={!!selectedTemplate ? "None selected" : "Select Telemedicine Categories"}
              />
              <Button id="prescription-template-form-select-all-states-btn" type="primary" onClick={handleSelectAllStates}>Select All</Button>
              <Button id="prescription-template-form-clear-states-btn" type="danger" disabled={!states.selectedStates.length} onClick={handleClearStates}>Clear All</Button>
            </div>
          </FormItem>
          {!isSupply && (
            <FormItem
              span={12}
              label="Strength"
              name="strength"
              rules={[{ required: !isSupply }]}
            >
              <Select
                id="prescription-template-form-strength-select"
                placeholder="Select strength"
                onSelect={handleSelectStrength}
                options={availableMedicationStrengthsOptions}
              />
            </FormItem>
          )}
          <FormItem 
            span={12} 
            label={
              <StyledTooltip title="This pharmacy will override any state/category pharmacy config">Pharmacy</StyledTooltip>
            } 
            name="pharmacy"
          >
            <AutoComplete
              id="prescription-template-form-pharmacy-autocomplete"
              loading={pharmaciesLoading}
              defaultActiveFirstOption
              onSearch={searchPharmacies}
              onSelect={selectPharmacyById}
              options={pharmacyOptions}
              placeholder="Enter Pharmacy Name"
            />
          </FormItem>
          <FormItem span={8} name="pillCount" label="Quantity" hidden={!showQRD}>
            <Input id="prescription-template-form-quantity-input" type="text" placeholder="Enter quantity" min={1} />
          </FormItem>
          <FormItem span={8} name="refills" hidden={!showQRD}>
            <Input id="prescription-template-form-refills-input" type="number" placeholder="Enter Refills" min={0} />
          </FormItem>
          <FormItem span={8} label="Days Supply" name="daysSupply" hidden={!showQRD}>
            <Input id="prescription-template-form-days-supply-input" type="number" placeholder="Enter Days Supply" min={1} />
          </FormItem>
          <FormItem
            required
            rules={[{ required: true }]}
            span={24}
            label="Patient Directions"
            name="directions"
          >
            <TextArea id="prescription-template-form-directions-textarea" placeholder="Enter Patient Directions" />
          </FormItem>
          <FormItem
            span={24}
            label={
              <span>
                Pharmacy Notes
                <Tooltip
                  title={<PharmacyNotesTemplateKey />}
                  overlayStyle={{ maxWidth: 420 }}
                  placement="right"
                >
                  <TemplateKeyIcon id="prescription-template-form-pharmacy-notes-key-icon" />
                </Tooltip>
              </span>
            }
            name="pharmacyNotes"
          >
            <TextArea id="prescription-template-form-pharmacy-notes-textarea" placeholder="Enter Pharmacy Notes" />
          </FormItem>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.pharmacyNotes !== curr.pharmacyNotes}
          >
            {({ getFieldValue }) => {
              const miscased = findMiscasedSpecialVariables(getFieldValue('pharmacyNotes'))
              if (!miscased.length) return null
              return (
                <Col span={24} style={{ marginTop: -12, marginBottom: 8 }}>
                  <Typography.Text type="warning" style={{ textTransform: 'none' }}>
                    Variable name casing does not match a known template variable and will be treated as a free-text prompt:{' '}
                    {miscased.join(', ')}
                  </Typography.Text>
                </Col>
              )
            }}
          </Form.Item>
          <Form.Item
            span={24}
            name="DAW"
            valuePropName="checked"
            style={{ marginLeft: '7px' }}
          >
            <Checkbox>
              <Typography.Text>Dispense as Written</Typography.Text>
            </Checkbox>
          </Form.Item>
          <Form.Item
            span={24}
            name="qrd"
            valuePropName="checked"
            style={{ marginLeft: '7px' }}
            onChange={e => setShowQRD(e.target.checked)}
          >
            <Checkbox>
              <Typography.Text>Use Quantity, Refills, Days Supply</Typography.Text>
            </Checkbox>
          </Form.Item>
          <StyledButtonGroup>
            <Button id="prescription-template-form-cancel-btn" onClick={_handleFinish}>Cancel</Button>
            <Button
              id="prescription-template-form-submit-btn"
              style={{ background: '#7BAD7E', color: '#fff' }}
              icon={(isDuplicate || !selectedTemplate) && <PlusCircleOutlined />}
              htmlType="submit"
            >
              {selectedTemplate && !isDuplicate ? 'Save' : 'Create'}
            </Button>
          </StyledButtonGroup>
        </Row>
        <Form.Item hidden name="ndc" />
        <Form.Item hidden name="dispensableDrugId" />
        <Form.Item hidden name="supplyId" />
        <Form.Item hidden name="ingredients" />
        <Form.Item hidden name="dispenseUnitId" />
        <Form.Item hidden name="schedule" />
      </Form>
    </Spin>
  );
};
